import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

// Die Start-URL eines Kaltstarts wird genau EINMAL pro App-Lauf ausgewertet.
// `CapacitorApp.getLaunchUrl()` liefert dieselbe URL, solange die App läuft — ein
// zweiter Mount des Handlers würde also erneut dorthin navigieren.
// Genau das passierte am 9. Sep 2026 auf dem iPhone (Sentry JAVASCRIPT-REACT-KP):
// Einladungslink -> /register -> Registrierung -> die Onboarding-Weiche schickt auf
// /profile-setup und baut dabei den Handler ab -> auf /profile-setup baut sie ihn
// wieder auf -> er liest die alte Start-URL -> zurück auf /register -> ...
// Ergebnis: hunderte `history.replaceState` pro Sekunde ("SecurityError: Attempt to
// use history.replaceState() more than 100 times per 10 seconds"), das Profil-Setup
// war nicht erreichbar, die App zeigte nur noch blinkende Ladeanimationen.
let launchUrlHandled = false;

// Komponente für Deep-Links (iOS Universal Links / Android App Links):
// Öffnet die App über einen https-Link wie https://eachotter.de/register?invitation=...
// und leitet intern auf die passende Route (Pfad + Query übernehmen).
const DeepLinkHandler = () => {
  const navigate = useNavigate();
  // `navigate` ist in dieser React-Router-Version keine stabile Referenz: Sie wechselt
  // mit jeder Navigation. Als Effekt-Abhaengigkeit haette der Handler sich also bei
  // jedem Seitenwechsel neu aufgebaut (Listener ab- und wieder anmelden). Ueber die
  // Referenz laeuft der Effekt genau einmal - und nutzt trotzdem immer das aktuelle
  // `navigate`.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let listenerHandle = null;

    const handleUrl = (url) => {
      if (!url) return;
      try {
        const parsed = new URL(url);
        const target = `${parsed.pathname}${parsed.search}${parsed.hash}`;
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        // Nur sinnvolle In-App-Ziele ansteuern (nicht die nackte Root) und niemals
        // auf die Seite navigieren, auf der wir ohnehin schon stehen.
        if (target && target !== '/' && target !== current) {
          navigateRef.current(target);
        }
      } catch (err) {
        console.error('Deep-Link konnte nicht verarbeitet werden:', err);
      }
    };

    // Cold-Start: App wurde über einen Link gestartet (siehe launchUrlHandled oben)
    if (!launchUrlHandled) {
      launchUrlHandled = true;
      CapacitorApp.getLaunchUrl()
        .then((result) => {
          if (result && result.url) handleUrl(result.url);
        })
        .catch(() => {});
    }

    // Warm-Start: Link kommt, während die App bereits läuft
    CapacitorApp.addListener('appUrlOpen', (event) => {
      handleUrl(event?.url);
    }).then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      if (listenerHandle) listenerHandle.remove();
    };
  }, []);

  return null; // Diese Komponente rendert nichts
};

export default DeepLinkHandler;
